import {
  Box,
  Container,
  makeStyles,
  Typography,
  Chip,
  Button,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
} from "@material-ui/core";
import {
  GeneralModal,
  InfoBox,
  InfoLine,
} from "../components-molecules";
import { HeaderWithButtons } from "../components-molecules";
import { EditMerchantModal } from "../components-molecules";
import { EditMerchantUserModal } from "../components-molecules/EditMerchantUserModal";
import { InviteMerchantUserModal } from "../components-molecules/InviteMerchantUserModal";
import EditIcon from "@material-ui/icons/Edit";
import { ITheme, colours } from "../constants/theme";
import { useSelector } from "react-redux";
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { 
  getMerchantOrganisationDetails, 
  editMerchantOrganisation, 
  getMerchantShops,
  inviteMerchantUser,
  MerchantOrganisationDetailsResponseModel,
  MerchantShopListResponseModelShop,
  MerchantOrganisationUserDetails,
  InviteMerchantUserData,
  InviteMerchantResponseModel
} from "../redux/actions/merchantDetails";
import { useParams, useHistory } from "react-router-dom";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import { Searchbar } from "../components-molecules";
import { PrimaryButton } from "../components-atoms";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: ITheme) => ({
  container: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    maxWidth: "840px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  detailsContainer: {
    marginTop: "24px",
    paddingLeft: 0,
    paddingRight: 0,
  },
  shopsContainer: {
    marginTop: 16,
  },
  shopsSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    marginTop: 24,
    paddingLeft: 0,
    paddingRight: 0,
  },
  shopsSectionTitle: {
    fontWeight: 600,
    fontSize: '1.25rem',
    color: theme.palette.text.primary,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  shopList: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
    marginTop: 16,
    paddingBottom: 24,
  },
  shopContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: '16px 20px',
    border: `1px solid ${colours.dark12}`,
    borderRadius: 12,
    backgroundColor: '#fff',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
  },
  shopInfoContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    flex: 1,
  },
  shopNameContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  shopName: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.text.primary,
  },
  shopEmail: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  shopDetails: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.4,
  },
  shopStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 8,
  },
  statusChip: {
    fontSize: '0.7rem',
    height: 24,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
  archivedChip: {
    fontSize: '0.7rem',
    height: 24,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    backgroundColor: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    '&:hover': {
      backgroundColor: '#e8e8e8',
    },
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px',
    minHeight: '60px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
  searchContainer: {
    marginBottom: 16,
  },
  inviteButton: {
    fontSize: '0.875rem',
    fontWeight: 600,
    textTransform: 'none',
    padding: '8px 16px',
    borderRadius: 8,
  }
}));

export const MerchantDetailsPage = () => {
  const params: any = useParams();
  const history = useHistory();
  const userData = useSelector((state: any) => state.login);
  const isAdmin = useMemo(() => userData.role === "Admin", [userData]);
  const classes = useStyles();
  const { t } = useTranslation();

  const [merchant, setMerchant] = useState<MerchantOrganisationDetailsResponseModel>();
  const [shops, setShops] = useState<MerchantShopListResponseModelShop[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [shopsLoading, setShopsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMoreShops, setHasMoreShops] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalShops, setTotalShops] = useState(0);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  
  // User management state
  const [editUserModalVisible, setEditUserModalVisible] = useState(false);
  const [inviteUserModalVisible, setInviteUserModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<MerchantOrganisationUserDetails | null>(null);
  const [inviteUserLoading, setInviteUserLoading] = useState(false);
  const [inviteUserSuccessModalVisible, setInviteUserSuccessModalVisible] = useState(false);
  const [inviteUserSuccessModalDescription, setInviteUserSuccessModalDescription] = useState('');
  const getMerchantData = useCallback(() => {
    setInitialLoading(true);
    getMerchantOrganisationDetails(
      params.id,
      (res) => {
        setMerchant(res);
        setInitialLoading(false);
      },
      (err) => {
        setInitialLoading(false);
      }
    );
  }, [params.id]);

  const getShopsData = useCallback((query: string = '', page: number = 0, append: boolean = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setShopsLoading(true);
    }
    
    getMerchantShops(
      params.id,
      {
        Page: page,
        Size: 25,
        Query: query
      },
      (res) => {
        const newShops = res.Shops;
        setTotalShops(res.Count);
        
        if (append) {
          setShops(prevShops => {
            const updatedShops = [...prevShops, ...newShops];
            setHasMoreShops(updatedShops.length < res.Count);
            return updatedShops;
          });
          setLoadingMore(false);
        } else {
          setShops(newShops);
          setShopsLoading(false);
          setHasMoreShops(newShops.length < res.Count);
        }
        
        setCurrentPage(page);
      },
      (err) => {
        if (append) {
          setLoadingMore(false);
        } else {
          setShopsLoading(false);
        }
      }
    );
  }, [params.id]);

  useEffect(() => {
    getMerchantData();
    getShopsData();
  }, [getMerchantData, getShopsData]);


  const handleEdit = () => {
    setEditModalVisible(true);
  };

  const handleEditSubmit = (data: any) => {
    if (!merchant?.Id) return;
    
    setEditLoading(true);
    editMerchantOrganisation(
      merchant.Id,
      data,
      (response) => {
        setEditLoading(false);
        setEditModalVisible(false);
        // Refresh the merchant data
        getMerchantData();
      },
      (error) => {
        setEditLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleEditModalClose = () => {
    setEditModalVisible(false);
  };

  // User management handlers
  const handleEditUser = (user: MerchantOrganisationUserDetails) => {
    setSelectedUser(user);
    setEditUserModalVisible(true);
  };

  const handleEditUserModalClose = () => {
    setEditUserModalVisible(false);
    setSelectedUser(null);
  };

  const handleEditUserSuccess = () => {
    getMerchantData(); // Refresh the data
  };

  const handleInviteUser = () => {
    setInviteUserModalVisible(true);
  };

  const handleInviteUserModalClose = () => {
    setInviteUserModalVisible(false);
  };

  const handleInviteUserSubmit = (data: InviteMerchantUserData) => {
    setInviteUserLoading(true);
    inviteMerchantUser(
      params.id,
      data,
      (response: InviteMerchantResponseModel) => {
        setInviteUserLoading(false);
        setInviteUserModalVisible(false);
        setInviteUserSuccessModalDescription(`${t('merchantDetails.userAddedDescription')} ${response.Password}`);
        setInviteUserSuccessModalVisible(true);
        // Refresh the merchant data to show the new user
        getMerchantData();
      },
      (error) => {
        setInviteUserLoading(false);
        // TODO: Show error message to user
      }
    );
  };

  const handleInviteUserSuccessModalClose = () => {
    setInviteUserSuccessModalVisible(false);
    setInviteUserSuccessModalDescription('');
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(0);
    setHasMoreShops(true);
    getShopsData(query, 0, false);
  };

  const loadMoreShops = useCallback(() => {
    if (!loadingMore && hasMoreShops) {
      const nextPage = currentPage + 1;
      getShopsData(searchQuery, nextPage, true);
    }
  }, [loadingMore, hasMoreShops, currentPage, searchQuery, getShopsData]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreShops && !loadingMore) {
          loadMoreShops();
        }
      },
      { 
        threshold: 0.1,
        rootMargin: '200px' // Start loading 200px before the element comes into view
      }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [hasMoreShops, loadingMore, loadMoreShops]);

  // Fallback scroll listener for better reliability
  useEffect(() => {
    const handleScroll = () => {
      if (!hasMoreShops || loadingMore) return;
      
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      
      // Load more when user is within 300px of the bottom
      if (scrollTop + windowHeight >= documentHeight - 300) {
        loadMoreShops();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMoreShops, loadingMore, loadMoreShops]);

  const renderUser = (user: MerchantOrganisationUserDetails) => {
    return (
      <Card 
        key={user.Id} 
        className={classes.shopContainer}
      >
        <CardContent style={{ padding: '16px' }}>
          <Box className={classes.shopInfoContainer}>
            <Box className={classes.shopNameContainer}>
              <Typography className={classes.shopName}>
                {user.FirstName} {user.LastName}
              </Typography>
            </Box>
            
            <Typography className={classes.shopEmail}>
              {user.Email}
            </Typography>
            
            {user.PhoneNumber && (
              <Typography className={classes.shopDetails}>
                {user.PhoneNumber}
              </Typography>
            )}
          </Box>
          
          <Box className={classes.shopStatusContainer}>
            <IconButton
              size="small"
              onClick={() => handleEditUser(user)}
              style={{ 
                marginRight: 8,
                padding: 4
              }}
            >
              <EditIcon style={{ fontSize: 16, color: '#666' }} />
            </IconButton>
            <Chip
              label={t('merchantDetails.active')}
              color="primary"
              size="small"
              className={classes.statusChip}
            />
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderShop = (shop: MerchantShopListResponseModelShop) => {
    return (
      <Card 
        key={shop.Id} 
        className={classes.shopContainer}
        style={{ cursor: 'pointer' }}
        onClick={() => {
          // Navigate to shop details page
          window.location.href = `/admin/merchant/${params.id}/shop/${shop.Id}`;
        }}
      >
        <CardContent style={{ padding: '16px' }}>
          <Box className={classes.shopInfoContainer}>
            <Box className={classes.shopNameContainer}>
              <Typography className={classes.shopName}>
                {shop.Name}
              </Typography>
            </Box>
            
            <Typography className={classes.shopDetails}>
              {shop.City}, {shop.County}
            </Typography>
            <Typography className={classes.shopDetails}>
              {shop.PostalCode}
            </Typography>
          </Box>
          
          <Box className={classes.shopStatusContainer}>
            {shop.Archived ? (
              <Chip
                label={t('merchantDetails.archived')}
                size="small"
                className={classes.archivedChip}
                variant="outlined"
              />
            ) : (
              <Chip
                label={t('merchantDetails.active')}
                color="primary"
                size="small"
                className={classes.statusChip}
              />
            )}
          </Box>
        </CardContent>
      </Card>
    );
  };

  const renderHeader = () => {
    return (
      <HeaderWithButtons 
        title={`${t('merchantDetails.merchant')}: ${merchant?.Name || ''}`}
        handleBack={() => history.goBack()}
      >
        <HeaderWithButtons.ButtonArea>
          {isAdmin && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={handleEdit}
              startIcon={<EditIcon />}
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                padding: '8px 16px',
                borderRadius: 8,
              }}
            >
              {t('merchantDetails.editMerchant')}
            </Button>
          )}
        </HeaderWithButtons.ButtonArea>
      </HeaderWithButtons>
    );
  };

  return (
    <Box className={classes.container}>
      <Container className={classes.root}>
        {initialLoading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {renderHeader()}
            
            
            <Container className={classes.detailsContainer}>
              <InfoBox title={t('merchantDetails.merchantInformation')}>
                <InfoLine
                  name={t('merchantDetails.merchantName')}
                  value={merchant?.Name || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.phoneNumber')}
                  value={merchant?.PhoneNumber || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.infoEmail')}
                  value={merchant?.InfoEmail || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.vatNumber')}
                  value={merchant?.VatNumber || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.invoiceName')}
                  value={merchant?.InvoiceName || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.stripeAccountId')}
                  value={merchant?.StripeAccountId || "-"}
                />
                <InfoLine
                  name={t('merchantDetails.brandColour')}
                  value={
                    merchant?.BrandColour ? (
                      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Box
                          style={{
                            width: 20,
                            height: 20,
                            backgroundColor: "#" + merchant.BrandColour,
                            borderRadius: 4,
                            border: '1px solid #ccc'
                          }}
                        />
                        <Typography variant="body2">{merchant.BrandColour}</Typography>
                      </Box>
                    ) : "-"
                  }
                />
                <InfoLine
                  name={t('merchantDetails.hqAddress')}
                  value={
                    merchant?.Address ? (
                      <Box>
                        {merchant.Address.Line1 && <Typography variant="body2">{merchant.Address.Line1}</Typography>}
                        {merchant.Address.Line2 && <Typography variant="body2">{merchant.Address.Line2}</Typography>}
                        {merchant.Address.Line3 && <Typography variant="body2">{merchant.Address.Line3}</Typography>}
                        <Typography variant="body2">
                          {[merchant.Address.City, merchant.Address.County, merchant.Address.PostalCode]
                            .filter(Boolean)
                            .join(', ')}
                        </Typography>
                      </Box>
                    ) : "-"
                  }
                />
              </InfoBox>

              {merchant && (
                <Box>
                  <Box className={classes.shopsSectionHeader}>
                    <Typography variant="h6" className={classes.shopsSectionTitle}>
                      {t('merchantDetails.users')} ({merchant.Users?.length || 0})
                    </Typography>
                    {isAdmin && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={handleInviteUser}
                        className={classes.inviteButton}
                      >
                        {t('merchantDetails.addUser')}
                      </Button>
                    )}
                  </Box>
                  {merchant.Users && Array.isArray(merchant.Users) && merchant.Users.length > 0 && (
                    <Box className={classes.shopList}>
                      {merchant.Users.map(renderUser)}
                    </Box>
                  )}
                </Box>
              )}

              {merchant?.ShopSummaries && Array.isArray(merchant.ShopSummaries) && merchant.ShopSummaries.length > 0 && (
                <Box>
                  <Box className={classes.shopsSectionHeader}>
                    <Typography variant="h6" className={classes.shopsSectionTitle}>
                      {t('merchantDetails.shops')} ({totalShops || shops.length || 0})
                    </Typography>
                    <IconButton
                      data-automation="search-button"
                      color="primary"
                      onClick={() => setSearchOpen(!searchOpen)}
                    >
                      <SearchRoundedIcon color="primary" />
                    </IconButton>
                  </Box>
                  
                  {searchOpen && (
                    <Box className={classes.searchContainer}>
                      <Searchbar
                        query={searchQuery}
                        isOpen={searchOpen}
                        placeholder={t('merchantDetails.searchShops')}
                        onChange={(e) => {
                          handleSearchChange(e.target.value);
                        }}
                        onCancel={() => {
                          setSearchQuery('');
                          setSearchOpen(false);
                          getShopsData('');
                        }}
                      />
                    </Box>
                  )}
                  
                  {shopsLoading ? (
                    <Box style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                      <CircularProgress />
                    </Box>
                  ) : shops.length > 0 ? (
                    <>
                      <Box className={classes.shopList}>
                        {shops.map(renderShop)}
                      </Box>
                      {hasMoreShops && (
                        <div className={classes.loadMoreContainer} ref={loadMoreRef}>
                          {loadingMore && <CircularProgress size={24} />}
                        </div>
                      )}
                      {!hasMoreShops && shops.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          <Typography variant="body2">
                            {t('merchantDetails.allShopsLoaded')}
                          </Typography>
                        </div>
                      )}
                    </>
                  ) : (
                    <Box style={{ textAlign: 'center', padding: '40px' }}>
                      <Typography variant="h6" color="textSecondary">
                        {t('merchantDetails.noShopsFound')}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Container>
          </>
        )}
      </Container>
      
      <EditMerchantModal
        visible={editModalVisible}
        onClose={handleEditModalClose}
        onSubmit={handleEditSubmit}
        merchant={merchant}
        loading={editLoading}
      />
      
      <EditMerchantUserModal
        visible={editUserModalVisible}
        onClose={handleEditUserModalClose}
        onSuccess={handleEditUserSuccess}
        user={selectedUser}
        merchantId={params.id}
      />
      
      <InviteMerchantUserModal
        visible={inviteUserModalVisible}
        onClose={handleInviteUserModalClose}
        onSubmit={handleInviteUserSubmit}
        loading={inviteUserLoading}
      />

      <GeneralModal
        visible={inviteUserSuccessModalVisible}
        title={t('merchantDetails.userAdded')}
        description={inviteUserSuccessModalDescription}
        onClose={handleInviteUserSuccessModalClose}
        >
        <GeneralModal.ButtonArea>
            <PrimaryButton
            title="OK"
            onClick={handleInviteUserSuccessModalClose}
            style={{ marginLeft: '8px' }}
          />
          </GeneralModal.ButtonArea>
        </GeneralModal>
    </Box>
  );
};
